'use client'

/**
 * Purpose: Catalog / routine product tile with image, compatibility badge, ingredients,
 * buy link, and flag-for-review flow.
 * Inputs: `product` (id, name, image, link, ingredients, pregnancySafe…), optional
 * compatibility score/detail, routine-only slot/remove handlers, `onFlagSuccessAction`.
 * Outputs: An article element plus lazily mounted Ingredients / Flag / Compatibility modals; emits
 * `buy_now_clicked` telemetry. Product photo alt = product name (informative); the empty-image
 * placeholder and badge glyphs are `aria-hidden` (decorative).
 */
import Image from 'next/image'
import { useActionState, useState } from 'react'
import { captureBuyNowClicked } from '@lib/interaction-telemetry'
import { useLocale, useTranslations } from 'next-intl'
import { flagProduct } from '@/actions/contact'
import HoverGradientButton from '@components/buttons/HoverGradientButton/HoverGradientButton'
import { FlagModal } from '@components/modals/FlagModal/FlagModal'
import { IngredientsModal } from '@components/modals/IngredientsModal/IngredientsModal'
import { CompatibilityModal } from '@components/modals/CompatibilityModal/CompatibilityModal'
import { StarsBadge } from '@components/badges/StarsBadge/StarsBadge'
import { IngredientSafetyText } from '@components/texts/IngredientSafetyText/IngredientSafetyText'
import { formatPrice } from '@lib/format-price'
import type { CompatibilityMatch } from '@lib/products'
import type { RoutineTimeOfDay } from '@lib/models/routine'
import type { IngredientSafetyStatus, ProductIngredientItem } from '@lib/models/types'
import './ProductCard.css'

/** Minimal catalog shape the card needs — a subset of ProductItem. */
type Product = {
  id: string
  productName: string
  price: number | null
  currency: string | null
  image: string | null
  link: string | null
  category: string | null
  ingredients?: ProductIngredientItem[]
  pregnancySafe?: 'safe' | 'unsafe' | 'unknown'
  /** Phase 2 roll-up — null means not yet assessed. The card renders text only for 'safe'. */
  safetyStatus?: IngredientSafetyStatus | null
  safetyNote?: string | null
}

type Props = {
  product: Product
  compatibilityScore?: number
  compatibilityDetail?: CompatibilityMatch
  onFlagSuccessAction?: (productId: string) => void
  /** Routine-only: current AM/PM slot. Omitted on public catalog cards. */
  timeOfDay?: RoutineTimeOfDay
  onCycleTimeOfDay?: () => void
  /** Routine-only: remove from routine. Omitted on public catalog cards. */
  onRemove?: () => void
  removeLabel?: string
}

// Routine slot badge copy; brand-fixed abbreviations, so intentionally not translated.
const SLOT_LABEL: Record<RoutineTimeOfDay, string> = { am: 'AM', pm: 'PM', both: 'AM/PM' }

export function ProductCard({
  product,
  compatibilityScore,
  compatibilityDetail,
  onFlagSuccessAction,
  timeOfDay,
  onCycleTimeOfDay,
  onRemove,
  removeLabel,
}: Props) {
  const t = useTranslations('productCard')
  const tRec = useTranslations('productRecommendation')
  const locale = useLocale()
  const [state, formAction, isPending] = useActionState(flagProduct, null)
  const [showIngredients, setShowIngredients] = useState(false)
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [showCompatibility, setShowCompatibility] = useState(false)

  /* Derived view state: a successful flag action swaps the flag button for a thank-you line. */
  const flagged = (state as { success?: boolean } | null)?.success === true
  const imageUrl = product.image ?? null
  const ingredients = product.ingredients ?? []
  const hasIngredients = ingredients.length > 0
  // The card never shows a numeric price visually (it lives behind the affiliate
  // link), but a formatted price in the Buy button's aria-label lets screen reader
  // users distinguish one "View Price" link from the next in a product list.
  const formattedPrice = product.currency ? formatPrice(product.price, product.currency, locale) : null
  const showRoutineSlot = timeOfDay !== undefined && onCycleTimeOfDay !== undefined
  const showRoutineRemove = onRemove !== undefined

  return (
    <>
      <article id={`product-card-${product.id}`} className="product-card">
        <div id={`product-card-media-${product.id}`} className="product-card-media">
          {/* Informative image: the product photo is named by the catalog product name (product
              data, not a filename). The no-image placeholder is purely decorative. */}
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={product.productName}
              fill
              sizes="264px"
              className="object-contain"
            />
          ) : (
            <div id={`product-card-media-empty-${product.id}`} className="product-card-media-empty" aria-hidden />
          )}

          {/* Pregnancy-safe badge only for an explicit 'safe' verdict; unknown stays silent */}
          {product.pregnancySafe === 'safe' && (
            <span id={`product-card-pregnancy-badge-${product.id}`} className="product-card-badge product-card-badge-pregnancy">
              <svg viewBox="0 0 16 16" className="product-card-badge-icon" fill="none" aria-hidden>
                <circle cx="8" cy="8" r="8" fill="white" fillOpacity="0.25" />
                <path d="M4.5 8.2l2.2 2.2 4.8-4.8" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('pregnancySafeBadge')}
            </span>
          )}

          {/* Routine-only AM/PM cycle button (aria-label carries the current slot) */}
          {showRoutineSlot && (
            <button
              id={`product-card-slot-${product.id}`}
              type="button"
              onClick={onCycleTimeOfDay}
              title={t('slotCycleTitle')}
              aria-label={t('slotCycleAria', { slot: SLOT_LABEL[timeOfDay] })}
              className="product-card-badge product-card-badge-slot"
            >
              {SLOT_LABEL[timeOfDay]}
            </button>
          )}

          {/* Routine-only remove control — icon-only, so it needs a translated aria-label */}
          {showRoutineRemove && (
            <button
              id={`product-card-remove-${product.id}`}
              type="button"
              onClick={onRemove}
              aria-label={removeLabel ?? t('removeAria')}
              className="product-card-remove"
            >
              <svg viewBox="0 0 12 12" className="product-card-remove-icon" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                <path d="M1 1l10 10M11 1L1 11" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        <div id={`product-card-body-${product.id}`} className="product-card-body">
          <p id={`product-card-name-${product.id}`} className="question-bold product-card-name">{product.productName}</p>

          {/* Compatibility badge (opens the modal when detail is available) + ingredients trigger */}
          <div id={`product-card-ingredients-row-${product.id}`} className="product-card-ingredients-row">
            {compatibilityScore !== undefined && (
              <StarsBadge
                score={compatibilityScore}
                onClick={compatibilityDetail ? () => setShowCompatibility(true) : undefined}
              />
            )}

            {hasIngredients && (
              <button
                id={`product-card-ingredients-${product.id}`}
                type="button"
                onClick={() => setShowIngredients(true)}
                className="product-card-ingredients"
              >
                {t('viewIngredients')}
              </button>
            )}
          </div>

          {/* Phase 2 FR-05: plain-text safety status (never icon-only). Like the pregnancy badge,
              only an explicit 'safe' roll-up is shown; caution / avoid / unassessed stay silent. */}
          {product.safetyStatus === 'safe' && (
            <div id={`product-card-safety-${product.id}`} className="mt-2">
              <IngredientSafetyText
                safetyStatus={product.safetyStatus}
                safetyNote={product.safetyNote}
                surface="product_page"
                productId={product.id}
                idSuffix={`product-${product.id}`}
              />
            </div>
          )}

          {/* Footer: affiliate buy link (tracked) and flag-for-review entry point */}
          <div id={`product-card-footer-${product.id}`} className="product-card-footer">
            {product.link && (
              <HoverGradientButton
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                className="product-card-cta"
                ariaLabel={
                  formattedPrice
                    ? t('productCtaAria', { name: product.productName, price: formattedPrice })
                    : undefined
                }
                onClick={() => captureBuyNowClicked(product.id)}
              >
                {t('productCta')}
              </HoverGradientButton>
            )}

            {/* Flag button is replaced by the confirmation once the action succeeds */}
            {flagged ? (
              <p id={`product-card-flag-message-${product.id}`} className="product-card-flag-message">{tRec('flagMessage')}</p>
            ) : (
              <button
                id={`product-card-flag-${product.id}`}
                type="button"
                onClick={() => setShowFlagModal(true)}
                aria-label={t('flagAriaLabel')}
                title={t('flagTooltip')}
                className="product-card-flag"
              >
                {t('flagTooltip')}
              </button>
            )}
          </div>
        </div>
      </article>

      {/* Modals mount lazily on demand; each owns its focus trap and close handling */}
      {showIngredients && hasIngredients && (
        <IngredientsModal
          productName={product.productName}
          ingredients={ingredients}
          onClose={() => setShowIngredients(false)}
        />
      )}

      {showFlagModal && !flagged && (
        <FlagModal
          productId={product.id}
          productName={product.productName}
          formAction={formAction}
          isPending={isPending}
          onClose={() => setShowFlagModal(false)}
          onSubmit={() => {
            setShowFlagModal(false)
            onFlagSuccessAction?.(product.id)
          }}
        />
      )}

      {/* Compatibility breakdown — only reachable when the badge had detail to show */}
      {showCompatibility && compatibilityDetail && (
        <CompatibilityModal
          productName={product.productName}
          score={compatibilityDetail.score}
          matches={compatibilityDetail.matches}
          onClose={() => setShowCompatibility(false)}
        />
      )}
    </>
  )
}
